import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit3, Trash2, Save, X, ChevronUp, ChevronDown } from 'lucide-react';
import timetableService from '../../services/timetableService';
import '../../styles/TeacherSettings.css';

const TeacherSettings = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [mode, setMode] = useState('Modify'); // Add, Modify, Delete, Cancel, Save
  const [formData, setFormData] = useState({
    staff_code: '',
    teacher_key: '',
    name_english: '',
    name_chinese: '',
    short_name_english: '',
    short_name_chinese: '',
    max_load_per_day_normal: 6,
    max_load_per_day_special: 4
  });

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    try {
      const data = await timetableService.getTeachers();
      setTeachers(data);
      if (data.length > 0 && !selectedTeacher) {
        setSelectedTeacher(data[0]);
        setFormData({
          staff_code: data[0].staff_code || '',
          teacher_key: data[0].teacher_key || '',
          name_english: data[0].name_english || '',
          name_chinese: data[0].name_chinese || '',
          short_name_english: data[0].short_name_english || '',
          short_name_chinese: data[0].short_name_chinese || '',
          max_load_per_day_normal: data[0].max_load_per_day_normal || 6,
          max_load_per_day_special: data[0].max_load_per_day_special || 4
        });
      }
    } catch (error) {
      console.error('Error loading teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      staff_code: teacher.staff_code || '',
      teacher_key: teacher.teacher_key || '',
      name_english: teacher.name_english || '',
      name_chinese: teacher.name_chinese || '',
      short_name_english: teacher.short_name_english || '',
      short_name_chinese: teacher.short_name_chinese || '',
      max_load_per_day_normal: teacher.max_load_per_day_normal || 6,
      max_load_per_day_special: teacher.max_load_per_day_special || 4
    });
    setMode('Modify');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdd = () => {
    setMode('Add');
    setFormData({
      staff_code: '',
      teacher_key: '',
      name_english: '',
      name_chinese: '',
      short_name_english: '',
      short_name_chinese: '',
      max_load_per_day_normal: 6,
      max_load_per_day_special: 4
    });
  };

  const handleModify = () => {
    setMode('Modify');
  };

  const handleDelete = async () => {
    if (selectedTeacher && window.confirm('確定要刪除此教師嗎？')) {
      try {
        await timetableService.deleteTeacher(selectedTeacher.id);
        await loadTeachers();
        setMode('Modify');
      } catch (error) {
        console.error('Error deleting teacher:', error);
      }
    }
  };

  const handleCancel = () => {
    if (selectedTeacher) {
      setFormData({
        staff_code: selectedTeacher.staff_code || '',
        teacher_key: selectedTeacher.teacher_key || '',
        name_english: selectedTeacher.name_english || '',
        name_chinese: selectedTeacher.name_chinese || '',
        short_name_english: selectedTeacher.short_name_english || '',
        short_name_chinese: selectedTeacher.short_name_chinese || '',
        max_load_per_day_normal: selectedTeacher.max_load_per_day_normal || 6,
        max_load_per_day_special: selectedTeacher.max_load_per_day_special || 4
      });
    }
    setMode('Modify');
  };

  const handleSave = async () => {
    try {
      if (mode === 'Add') {
        await timetableService.createTeacher(formData);
      } else if (mode === 'Modify' && selectedTeacher) {
        await timetableService.updateTeacher(selectedTeacher.id, formData);
      }
      await loadTeachers();
      setMode('Modify');
    } catch (error) {
      console.error('Error saving teacher:', error);
    }
  };

  const incrementNormalLoad = () => {
    setFormData(prev => ({ ...prev, max_load_per_day_normal: prev.max_load_per_day_normal + 1 }));
  };

  const decrementNormalLoad = () => {
    setFormData(prev => ({ ...prev, max_load_per_day_normal: Math.max(1, prev.max_load_per_day_normal - 1) }));
  };

  const incrementSpecialLoad = () => {
    setFormData(prev => ({ ...prev, max_load_per_day_special: prev.max_load_per_day_special + 1 }));
  };

  const decrementSpecialLoad = () => {
    setFormData(prev => ({ ...prev, max_load_per_day_special: Math.max(0, prev.max_load_per_day_special - 1) }));
  };
  if (loading) {
    return (
      <div className="teacher-settings-container">
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
            <p className="loading-text">載入教師資料中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-settings-container">
      {/* Header */}
      <div className="teacher-settings-header">
        <h2>Teachers [TBM0130E]</h2>
        <p className="subtitle">教師設定 - 管理教師資料和授課節數</p>
      </div>

      {/* Main Content */}
      <div className="teacher-settings-content">
        {/* Teacher Form Panel */}
        <div className="teacher-form-panel">
          <h3 className="teacher-form-title">Teachers [TBM0130E]</h3>
          
          <div className="teacher-form-grid">
            {/* Staff Code */}
            <div className="teacher-form-group">
              <label className="teacher-form-label">Staff Code:</label>
              <input
                type="text"
                value={formData.staff_code}
                onChange={(e) => handleInputChange('staff_code', e.target.value)}
                className="teacher-form-input"
                placeholder="LTM"
              />
            </div>

            {/* Key */}
            <div className="teacher-form-group">
              <label className="teacher-form-label">Key:</label>
              <input
                type="text"
                value={formData.teacher_key}
                onChange={(e) => handleInputChange('teacher_key', e.target.value)}
                className="teacher-form-input"
                placeholder="LTM"
              />
            </div>

            {/* Name (Eng) */}
            <div className="teacher-form-group">
              <label className="teacher-form-label">Name (Eng):</label>
              <input
                type="text"
                value={formData.name_english}
                onChange={(e) => handleInputChange('name_english', e.target.value)}
                className="teacher-form-input"
                placeholder="LUK TAI MING"
              />
            </div>

            {/* Name (Chi) */}
            <div className="teacher-form-group">
              <label className="teacher-form-label">Name (Chi):</label>
              <input
                type="text"
                value={formData.name_chinese}
                onChange={(e) => handleInputChange('name_chinese', e.target.value)}
                className="teacher-form-input"
                placeholder="陸大明"
              />
            </div>

            {/* Short Name (Eng) */}
            <div className="teacher-form-group">
              <label className="teacher-form-label">Short Name (Eng):</label>
              <input
                type="text"
                value={formData.short_name_english}
                onChange={(e) => handleInputChange('short_name_english', e.target.value)}
                className="teacher-form-input"
                placeholder="LTM"
              />
            </div>

            {/* Short Name (Chi) */}
            <div className="teacher-form-group">
              <label className="teacher-form-label">(Chi):</label>
              <input
                type="text"
                value={formData.short_name_chinese}
                onChange={(e) => handleInputChange('short_name_chinese', e.target.value)}
                className="teacher-form-input"
                placeholder="明"
              />
            </div>

            {/* Maximum Load/Day */}
            <div className="teacher-form-group full-width">
              <label className="teacher-form-label">Maximum Load/Day:</label>
              <div className="teacher-number-input-group">
                <input
                  type="number"
                  value={formData.max_load_per_day_normal}
                  onChange={(e) => handleInputChange('max_load_per_day_normal', parseInt(e.target.value) || 6)}
                  className="teacher-form-input"
                  min="1"
                />
                <div className="teacher-number-controls">
                  <button type="button" onClick={incrementNormalLoad} className="teacher-number-btn">
                    <ChevronUp size={12} />
                  </button>
                  <button type="button" onClick={decrementNormalLoad} className="teacher-number-btn">
                    <ChevronDown size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* Normal/Special Load */}
            <div className="teacher-form-group full-width">
              <div className="teacher-load-section">
                <h4 className="teacher-load-section-title">Load Options:</h4>
                <div className="teacher-load-options">
                  <div className="teacher-load-option">
                    <label className="teacher-load-option-label">Normal</label>
                    <div className="teacher-number-input-group">
                      <input
                        type="number"
                        value={formData.max_load_per_day_normal}
                        onChange={(e) => handleInputChange('max_load_per_day_normal', parseInt(e.target.value) || 6)}
                        className="teacher-form-input"
                        min="1"
                      />
                      <div className="teacher-number-controls">
                        <button type="button" onClick={incrementNormalLoad} className="teacher-number-btn">
                          <ChevronUp size={12} />
                        </button>
                        <button type="button" onClick={decrementNormalLoad} className="teacher-number-btn">
                          <ChevronDown size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="teacher-load-option">
                    <label className="teacher-load-option-label">Special</label>
                    <div className="teacher-number-input-group">
                      <input
                        type="number"
                        value={formData.max_load_per_day_special}
                        onChange={(e) => handleInputChange('max_load_per_day_special', parseInt(e.target.value) || 4)}
                        className="teacher-form-input"
                        min="0"
                      />
                      <div className="teacher-number-controls">
                        <button type="button" onClick={incrementSpecialLoad} className="teacher-number-btn">
                          <ChevronUp size={12} />
                        </button>
                        <button type="button" onClick={decrementSpecialLoad} className="teacher-number-btn">
                          <ChevronDown size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons Panel */}
        <div className="action-buttons-panel">
          <button
            onClick={() => window.history.back()}
            className="action-button secondary"
          >
            <X size={16} />
            Close
          </button>
          <button
            onClick={handleAdd}
            className="action-button success"
          >
            <Plus size={16} />
            Add
          </button>
          <button
            onClick={handleModify}
            className="action-button primary"
          >
            <Edit3 size={16} />
            Modify
          </button>
          <button
            onClick={handleDelete}
            className="action-button danger"
          >
            <Trash2 size={16} />
            Delete
          </button>
          <button
            onClick={handleCancel}
            className="action-button secondary"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button success"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Teacher List Panel */}
      <div className="teacher-list-panel">
        <div className="teacher-list-header">
          <h3 className="teacher-list-title">Current Teacher List:</h3>
          <span className="teacher-list-count">{teachers.length}</span>
        </div>
        
        <div className="teacher-list">
          {teachers.map((teacher, index) => (
            <div
              key={teacher.id}
              onClick={() => handleTeacherSelect(teacher)}
              className={`teacher-item ${selectedTeacher?.id === teacher.id ? 'selected' : ''}`}
            >
              <div className="teacher-item-code">
                {teacher.staff_code}-{teacher.teacher_key}-{teacher.name_english}{teacher.name_chinese}-{teacher.short_name_english}-{teacher.short_name_chinese}
              </div>
              <div className="teacher-item-details">
                Staff Code: {teacher.staff_code} | Key: {teacher.teacher_key} | Name: {teacher.name_english} | Chinese: {teacher.name_chinese}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mode Indicator */}
      <div className="mode-indicator">
        Mode: {mode}
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">教師設定指引</h4>
        <div className="instructions-content">
          <p><strong>1. 核對教師的資料：</strong> 每天最多授課節數是在學校/時間表功能設定，本功能只可修改教師的中、英文簡稱及索引名稱。</p>
          <p><strong>2. 系統如發現索引名稱(Key)相同：</strong> 會將後來的教師索引名稱改為以「~」符號為首的教師索引名稱，用者可按「編修」(Modify)鍵進行修改。</p>
          <p><strong>3. 將以「~」符號為索引名稱的教師索引改為較易明白的名稱。</strong></p>
          <p><strong>4. 輸入教師的中文簡稱。</strong></p>
          <p><strong>5. 輸入完成後，按「儲存」(Save)鍵紀錄資料。</strong></p>
        </div>
      </div>
    </div>
  );
};

export default TeacherSettings;
